import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import FormTextInput from '@components/form/FormTextInput';
import { useAccountContext } from '@lib/hooks';
import { validateEmail } from '@lib/utils';

type Props = {
  showLoginForm: boolean;
  isFreeTicket: boolean;
  setShowLoginForm: any;
  isEmailDuplicate: boolean;
  setIsEmailDuplicate: any;
};

function UserInfoSection({
  showLoginForm,
  isFreeTicket,
  setShowLoginForm,
  isEmailDuplicate,
  setIsEmailDuplicate,
}: Props) {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { user, uid } = useAccountContext();

  const { formState, register, setValue } = useFormContext();

  const [showPwd, setShowPwd] = useState(false);

  const { errors }: { errors: any } = formState;

  const userLoggedInEmail = uid && user?.email;
  const userLoggedInFirstName = useMemo(
    () => uid && user?.firstName,
    [uid, user?.firstName]
  );
  const userLoggedInLastName = useMemo(
    () => uid && user?.lastName,
    [uid, user?.lastName]
  );

  const checkEmailDuplicate = useCallback(
    async (event: any) => {
      const email = event.target.value;

      if (!validateEmail(email)) return;
      const result = await fetch('/api/checkEmailDuplicate', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });
      if (result.status === 200) {
        const response = await result.json();

        const { isEmailDuplicate } = response;
        setIsEmailDuplicate(isEmailDuplicate);
      }
    },
    [setIsEmailDuplicate]
  );

  useEffect(() => {
    if (userLoggedInFirstName) {
      setValue('firstName', userLoggedInFirstName);
    }
  }, [setValue, userLoggedInFirstName]);

  useEffect(() => {
    if (userLoggedInLastName) {
      setValue('lastName', userLoggedInLastName);
    }
  }, [setValue, userLoggedInLastName]);

  return (
    <>
      <Heading
        fontSize="l"
        fontWeight="light"
        pb={4}
        width="full"
        borderBottom="1px"
        borderColor="gray.300"
        mt={4}
      >
        {t('Buyer Information')}
      </Heading>

      <Flex
        pt={4}
        pb={4}
        flexWrap="wrap"
        width="100%"
        justifyContent={{ base: 'flex-start', md: 'space-between' }}
      >
        {showLoginForm ? null : (
          <>
            {language === 'zh-TW' ? (
              <HStack spacing={2} w="full" alignItems="flex-center">
                <FormTextInput label={t('Last Name')} id="lastName" />
                <FormTextInput label={t('First Name')} id="firstName" />
              </HStack>
            ) : (
              <HStack spacing={2} w="full" alignItems="flex-center">
                <FormTextInput label={t('First Name')} id="firstName" />
                <FormTextInput label={t('Last Name')} id="lastName" />
              </HStack>
            )}
            <FormControl
              id="email"
              mb={2}
              isRequired
              width={{ base: '100%', md: '49%' }}
            >
              <FormLabel>Email</FormLabel>
              {isEmailDuplicate && (
                <Text fontSize="xs" color="red" fontStyle="italic">
                  {t(
                    'Email exists, please login in first or choose another email'
                  )}
                </Text>
              )}
              {userLoggedInEmail ? (
                <Input
                  {...register('email')}
                  isReadOnly={!!userLoggedInEmail}
                  isDisabled={!!userLoggedInEmail}
                  autoComplete={userLoggedInEmail ? 'off' : 'on'}
                  value={userLoggedInEmail}
                />
              ) : (
                <>
                  <Input
                    {...register('email')}
                    autoComplete={userLoggedInEmail ? 'off' : 'on'}
                    defaultValue={userLoggedInEmail}
                    onBlur={checkEmailDuplicate}
                  />
                  <Text fontSize="xs" color="brand">
                    {t('Create account after buying')}
                  </Text>
                </>
              )}
            </FormControl>

            {!isFreeTicket && (
              <FormControl
                id="mobile"
                mb={2}
                isRequired
                width={{ base: '100%', md: '49%' }}
              >
                <FormLabel>{t('Mobile')}</FormLabel>
                <Input {...register('mobile')} />
              </FormControl>
            )}

            {!userLoggedInEmail && (
              <FormControl
                id="password"
                mb={2}
                isRequired
                width={{ base: '100%', md: '49%' }}
              >
                <FormLabel>{t('Password')}</FormLabel>
                {errors && errors.password && errors.password.message && (
                  <Text
                    fontWeight="bold"
                    fontSize="sm"
                    fontStyle="italic"
                    color="red"
                    mt={2}
                    mb={1}
                  >
                    {errors.password.message}
                  </Text>
                )}

                <InputGroup size="md">
                  <Input
                    {...register('password', {
                      validate: (value: string) => {
                        return value === ''
                          ? true
                          : value.length > 6 ||
                              'Password should have more than 6 characters';
                      },
                    })}
                    type={showPwd ? 'text' : 'password'}
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowPwd((showPwd) => !showPwd)}
                    >
                      {showPwd ? 'Hide' : 'Show'}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <Text fontSize="xs" color="brand">
                  {t('We will use this password to sign you up in Kaik.Live')}
                </Text>
              </FormControl>
            )}
          </>
        )}

        {userLoggedInEmail ? null : showLoginForm ? (
          <Button
            onClick={() => setShowLoginForm(!showLoginForm)}
            size="sm"
            colorScheme="blue"
            variant="ghost"
            width="full"
          >
            {t("Don't have account? Click to Directly purchase")}
          </Button>
        ) : (
          <Button
            onClick={() => setShowLoginForm(!showLoginForm)}
            size="sm"
            colorScheme="blue"
            variant="ghost"
            width="full"
            mt="2"
          >
            {t('Already have account? Click to login')}
          </Button>
        )}
      </Flex>
    </>
  );
}

export default UserInfoSection;
